import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Shield, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Blocks the workspace until an invited or admin-reset user chooses a new
 * password. Uses AlertDialog (no X, no outside-click dismiss) so the flow
 * cannot be escaped.
 */
export default function ForcePasswordChange() {
  const { user, refreshMe } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const mustChange = !!user?.require_password_change;
  if (!mustChange) return null;

  const tooShort = next.length > 0 && next.length < 8;
  const mismatch = confirm.length > 0 && confirm !== next;
  const sameAsCurrent = next.length > 0 && next === current;
  const canSubmit = !!current && next.length >= 8 && confirm === next && !sameAsCurrent;

  async function submit(e) {
    e.preventDefault();
    if (!canSubmit || saving) return;
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      await refreshMe();
      toast.success("Password updated — you're all set.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <AlertDialog open={true}>
      <AlertDialogContent data-testid="force-password-dialog" className="sm:max-w-md">
        <AlertDialogHeader>
          <div className="flex items-center gap-2 mb-1 text-blue-600">
            <Shield className="w-4 h-4" strokeWidth={2.25} />
            <span className="text-[11px] uppercase tracking-wider font-semibold">
              One-time setup
            </span>
          </div>
          <AlertDialogTitle className="text-lg tracking-tight">
            Choose a new password
          </AlertDialogTitle>
          <AlertDialogDescription className="text-[13px]">
            You signed in with a temporary password. Pick a new one to finish setting up
            your <span className="font-medium text-slate-700">{user.email}</span> account.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <form onSubmit={submit} className="space-y-4 mt-2">
          <div className="space-y-1">
            <Label className="text-[11px] uppercase tracking-wider text-slate-500">
              Current (temporary) password
            </Label>
            <Input
              data-testid="force-pw-current"
              type="password"
              autoFocus
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
            />
          </div>

          <div className="space-y-1">
            <Label className="text-[11px] uppercase tracking-wider text-slate-500">
              New password
            </Label>
            <Input
              data-testid="force-pw-new"
              type="password"
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
            />
            <div className="text-[11px] text-slate-400 flex items-center gap-1.5">
              {next.length >= 8 ? (
                <CheckCircle2 className="w-3 h-3 text-emerald-500" />
              ) : (
                <span className="w-3 h-3 rounded-full border border-slate-300" />
              )}
              At least 8 characters
            </div>
            {sameAsCurrent && (
              <div className="text-[11px] text-rose-600">
                New password must be different from the temporary one.
              </div>
            )}
            {tooShort && !sameAsCurrent && (
              <div className="text-[11px] text-rose-600">Too short — use 8+ characters.</div>
            )}
          </div>

          <div className="space-y-1">
            <Label className="text-[11px] uppercase tracking-wider text-slate-500">
              Confirm new password
            </Label>
            <Input
              data-testid="force-pw-confirm"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
            {mismatch && (
              <div className="text-[11px] text-rose-600">Passwords don't match.</div>
            )}
          </div>

          <Button
            type="submit"
            disabled={!canSubmit || saving}
            data-testid="force-pw-submit"
            className="w-full bg-blue-600 hover:bg-blue-700"
          >
            {saving ? "Saving…" : "Save new password"}
          </Button>
        </form>
      </AlertDialogContent>
    </AlertDialog>
  );
}
